// File: src/components/RaceManagement.js
// Race CRUD component with import buttons - FIXED

import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, Upload, FileSpreadsheet, Timer, Save, X, Lock, RotateCcw, MoreVertical } from 'lucide-react';
import { API_BASE_URL } from '../App';
import { calculatePointsForPosition, calculateQualificationPoints, formatDateForDisplay, getTodayISO } from '../utils/utils';
import ExcelImport from './ExcelImport';
import MyRCMImport from './MyRCMImport';
import LiveRCImport from './LiveRCImport';
import { usePermissions } from '../hooks/usePermissions';
import { useLanguage } from '../contexts/LanguageContext';
import { handleUnauthorized } from '../utils/session';

function RaceManagement({ currentClass, token, userRole, loadSeries }) {
  const permissions = usePermissions(userRole);
  const { t } = useLanguage();
  const [showRaceModal, setShowRaceModal] = useState(false);
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [showExcelImport, setShowExcelImport] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [raceForm, setRaceForm] = useState({ name: '', date: '', sequence_number: '' });
  const [selectedRace, setSelectedRace] = useState(null);
  const [rankings, setRankings] = useState([]);
  const [importRaceId, setImportRaceId] = useState(null);

  // Import from MyRCM - Fixed state variables
  const [showMyRCMImportModal, setShowMyRCMImportModal] = useState(false);
  const [myrcmImportRaceId, setMyrcmImportRaceId] = useState(null);

  // Import from LiveRC - the other timing system results arrive from
  const [showLiveRCImportModal, setShowLiveRCImportModal] = useState(false);
  const [livercImportRaceId, setLivercImportRaceId] = useState(null);

  // Menu state for each race
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRefs = useRef({});

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (openMenuId && menuRefs.current[openMenuId] && !menuRefs.current[openMenuId].contains(event.target)) {
        setOpenMenuId(null);
      }
    }

    if (openMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openMenuId]);

  const createRace = async () => {
    if (!currentClass) {
      alert(t('pleaseSelectSeriesFirst'));
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/races`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: raceForm.name,
          date: raceForm.date || getTodayISO(),
          series_class_id: currentClass.id,
          sequence_number: raceForm.sequence_number ? raceForm.sequence_number.trim() : null
        })
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        setShowRaceModal(false);
        setRaceForm({ name: '', date: '', sequence_number: '' });
      } else {
        const error = await response.json();
        alert(t('errorCreatingRace') + (error.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating race:', error);
      alert(t('connectionError'));
    }
  };

  const updateRace = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/races/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(raceForm)
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        setShowRaceModal(false);
        setEditingItem(null);
        setRaceForm({ name: '', date: '', sequence_number: '' });
      } else {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
      }
    } catch (error) {
      console.error('Error updating race:', error);
      alert(t('connectionError'));
    }
  };

  const deleteRace = async (id) => {
    if (!window.confirm(t('deleteThisRace'))) return;

    try {
      const response = await fetch(`${API_BASE_URL}/races/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(t('saveFailed') + (data.error ? `: ${data.error}` : ''));
        return;
      }
      loadSeries();
    } catch (error) {
      console.error('Error deleting race:', error);
      alert(t('connectionError'));
    }
  };

  const resetRaceResults = async (id) => {
    if (!window.confirm(t('resetRaceResultsConfirm'))) return;

    try {
      const response = await fetch(`${API_BASE_URL}/races/${id}/rankings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rankings: [] })
      });

      if (handleUnauthorized(response)) return;

      if (response.ok) {
        loadSeries();
        alert(t('raceResultsReset'));
      } else {
        alert(t('failedToResetResults'));
      }
    } catch (error) {
      console.error('Error resetting race results:', error);
      alert(t('failedToResetResults'));
    }
  };

  const openRankingModal = (race) => {
    setSelectedRace(race);
    const initialRankings = currentClass?.drivers?.map(driver => {
      const existing = race.rankings?.find(r => r.driver_id === driver.id);
      return {
        driver_id: driver.id,
        driver_name: driver.name,
        position: existing?.position || '',
        qualification_position: existing?.qualification_position || '',
        points: existing?.points || 0,
        qualification_points: existing?.qualification_points || 0
      };
    }) || [];
    
    setRankings(initialRankings);
    setShowRankingModal(true);
  };

  const updateRanking = (driverId, field, value) => {
    setRankings(prev => prev.map(r => {
      if (r.driver_id === driverId) {
        const updated = { ...r, [field]: value };
        
        if (field === 'position' && value) {
          updated.points = calculatePointsForPosition(parseInt(value));
        }
        
        if (field === 'qualification_position' && value && currentClass?.calculate_qualification_points) {
          updated.qualification_points = calculateQualificationPoints(parseInt(value));
        } else if (field === 'qualification_position' && !value) {
          updated.qualification_points = 0;
        }
        
        return updated;
      }
      return r;
    }));
  };

  const saveRankings = async () => {
    try {
      // Prepare rankings in backend format
      const rankingsData = rankings
        .filter(r => r.position)  // Only include drivers with positions
        .map(r => ({
          driverId: r.driver_id,  // camelCase!
          position: parseInt(r.position),
          qualificationPosition: r.qualification_position ? parseInt(r.qualification_position) : null,  // camelCase!
          points: r.points,
          qualificationPoints: r.qualification_points  // camelCase!
        }));

      const response = await fetch(`${API_BASE_URL}/races/${selectedRace.id}/rankings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ rankings: rankingsData })
      });

      if (handleUnauthorized(response)) return;
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        alert(t('failedToSaveRankings') + (data.error ? `: ${data.error}` : ''));
        return;
      }

      loadSeries();
      setShowRankingModal(false);
    } catch (error) {
      console.error('Error saving rankings:', error);
      alert(t('failedToSaveRankings'));
    }
  };

  return (
    <>
      <div className="bg-paper rounded-lg p-6 mb-6 border border-line">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-ink">{t('races')}</h2>
          <button
            onClick={() => {
              setEditingItem(null);
              setRaceForm({ name: '', date: '', sequence_number: '' });
              setShowRaceModal(true);
            }}
            className="bg-accent hover:opacity-90 text-accent-ink px-4 py-2 rounded font-semibold flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            {t('addRace')}
          </button>
        </div>

        <div className="space-y-2">
          {currentClass.races?.map(race => (
            <div key={race.id} className="flex items-center justify-between p-3 bg-paper-alt rounded relative">
              <div>
                <span className="text-ink font-medium">{race.name}</span>
                {race.date && (
                  <span className="text-ink-muted text-sm ml-3">
                    {formatDateForDisplay(race.date)}
                  </span>
                )}
              </div>

              {/* 3-Dots Menu for each race */}
              <div className="relative" ref={el => menuRefs.current[race.id] = el}>
                <button
                  onClick={() => setOpenMenuId(openMenuId === race.id ? null : race.id)}
                  className="bg-paper-alt hover:opacity-80 text-ink p-2 rounded-lg"
                  title={t('menu')}
                >
                  <MoreVertical className="w-5 h-5" />
                </button>

                {openMenuId === race.id && (() => {
                  const buttonRect = menuRefs.current[race.id]?.getBoundingClientRect();
                  const menuHeight = 400; // Approximate menu height
                  const spaceBelow = window.innerHeight - buttonRect.bottom;
                  const spaceAbove = buttonRect.top;
                  const positionAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;

                  return (
                    <div className="fixed bg-paper rounded-lg shadow-2xl border border-line py-2 min-w-[220px] max-h-[80vh] overflow-y-auto"
                         style={{
                           top: positionAbove
                             ? `${buttonRect.top - Math.min(menuHeight, spaceAbove - 20)}px`
                             : `${buttonRect.bottom + 8}px`,
                           right: (window.innerWidth - buttonRect.right) + 'px',
                           zIndex: 9999
                         }}>
                    {permissions.canEdit('race_rankings') ? (
                      <>
                        {/* Edit Results */}
                        <button
                          onClick={() => {
                            openRankingModal(race);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted"
                        >
                          <Edit className="w-4 h-4" />
                          {t('editResults')}
                        </button>

                        {/* Import from Excel */}
                        <button
                          onClick={() => {
                            setImportRaceId(race.id);
                            setShowExcelImport(true);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted"
                        >
                          <Upload className="w-4 h-4" />
                          {t('importFromExcel')}
                        </button>

                        {/* Import from MyRCM */}
                        <button
                          onClick={() => {
                            setMyrcmImportRaceId(race.id);
                            setShowMyRCMImportModal(true);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted"
                        >
                          <FileSpreadsheet className="w-4 h-4" />
                          {t('importFromMyRCM')}
                        </button>

                        {/* Import from LiveRC */}
                        <button
                          onClick={() => {
                            setLivercImportRaceId(race.id);
                            setShowLiveRCImportModal(true);
                            setOpenMenuId(null);
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted border-b border-line"
                        >
                          <Timer className="w-4 h-4" />
                          {t('importFromLiveRC')}
                        </button>
                      </>
                    ) : (
                      <div className="px-4 py-3 flex items-center gap-2 text-ink-muted text-sm border-b border-line">
                        <Lock className="w-4 h-4" />
                        <span>{t('resultsAdminOnly')}</span>
                      </div>
                    )}

                    {/* Edit Race */}
                    <button
                      onClick={() => {
                        setEditingItem(race);
                        setRaceForm({
                          name: race.name,
                          date: race.date ? race.date.split('T')[0] : '',
                          sequence_number: race.sequence_number || ''
                        });
                        setShowRaceModal(true);
                        setOpenMenuId(null);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-ink-muted"
                    >
                      <Edit className="w-4 h-4" />
                      {t('editRace')}
                    </button>

                    {/* Reset Race Results */}
                    <button
                      onClick={() => {
                        resetRaceResults(race.id);
                        setOpenMenuId(null);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-yellow-700 dark:text-yellow-400"
                    >
                      <RotateCcw className="w-4 h-4" />
                      {t('resetRaceResults')}
                    </button>

                    {/* Delete Race */}
                    <button
                      onClick={() => {
                        deleteRace(race.id);
                        setOpenMenuId(null);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-paper-alt transition-colors flex items-center gap-2 text-red-700 dark:text-red-400 border-t border-line"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('deleteRace')}
                    </button>
                  </div>
                  );
                })()}
              </div>
            </div>
          ))}
          {(!currentClass.races || currentClass.races.length === 0) && (
            <p className="text-ink-muted">{t('noRacesYet')}</p>
          )}
        </div>
      </div>

      {/* Race Modal */}
      {showRaceModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-paper rounded-lg p-6 max-w-md w-full border border-line">
            <h3 className="text-xl font-bold text-ink mb-4">
              {editingItem ? t('editRace') : t('newRace')}
            </h3>
            <div className="space-y-3">
              <input
                type="text"
                placeholder={t('raceName')}
                value={raceForm.name}
                onChange={(e) => setRaceForm({ ...raceForm, name: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              />
              <input
                type="date"
                value={raceForm.date}
                onChange={(e) => setRaceForm({ ...raceForm, date: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
              />
              <input
                type="text"
                placeholder={t('raceSequenceNumber')}
                value={raceForm.sequence_number}
                onChange={(e) => setRaceForm({ ...raceForm, sequence_number: e.target.value })}
                className="w-full px-4 py-2 bg-paper-alt border border-line rounded text-ink"
                maxLength="50"
              />
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => editingItem ? updateRace(editingItem.id) : createRace()}
                className="flex-1 bg-accent hover:opacity-90 text-accent-ink py-2 rounded font-semibold"
              >
                {editingItem ? t('update') : t('create')}
              </button>
              <button
                onClick={() => {
                  setShowRaceModal(false);
                  setEditingItem(null);
                }}
                className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-2 rounded font-semibold"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ranking Modal */}
      {showRankingModal && selectedRace && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-paper rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-line">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-ink">{t('editResultsFor')} {selectedRace.name}</h3>
              <button
                onClick={() => setShowRankingModal(false)}
                className="text-ink-muted hover:text-ink"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left text-ink-muted pb-3 pr-4">{t('driver')}</th>
                    <th className="text-center text-ink-muted pb-3 px-2">{t('position')}</th>
                    <th className="text-center text-ink-muted pb-3 px-2">{t('points')}</th>
                    {currentClass?.calculate_qualification_points && (
                      <>
                        <th className="text-center text-ink-muted pb-3 px-2">{t('qualPos')}</th>
                        <th className="text-center text-ink-muted pb-3 px-2">{t('qualPts')}</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rankings.map(ranking => (
                    <tr key={ranking.driver_id} className="border-b border-line">
                      <td className="py-3 pr-4">
                        <span className="text-ink">{ranking.driver_name}</span>
                      </td>
                      <td className="py-3 px-2">
                        <div className="flex justify-center">
                          <input
                            type="number"
                            min="1"
                            value={ranking.position}
                            onChange={(e) => updateRanking(ranking.driver_id, 'position', e.target.value)}
                            className="w-20 px-2 py-1 bg-paper-alt border border-line rounded text-ink text-center"
                          />
                        </div>
                      </td>
                      <td className="text-center py-3 px-2">
                        <span className="text-yellow-700 dark:text-yellow-400 font-mono-num">{ranking.points}</span>
                      </td>
                      {currentClass?.calculate_qualification_points && (
                        <>
                          <td className="py-3 px-2">
                            <div className="flex justify-center">
                              <input
                                type="number"
                                min="1"
                                max="5"
                                value={ranking.qualification_position}
                                onChange={(e) => updateRanking(ranking.driver_id, 'qualification_position', e.target.value)}
                                className="w-20 px-2 py-1 bg-paper-alt border border-line rounded text-ink text-center"
                              />
                            </div>
                          </td>
                          <td className="text-center py-3 px-2">
                            <span className="text-green-700 dark:text-green-400 font-mono-num">{ranking.qualification_points}</span>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={saveRankings}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded font-semibold flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {t('saveResults')}
              </button>
              <button
                onClick={() => setShowRankingModal(false)}
                className="flex-1 bg-paper-alt hover:opacity-80 text-ink py-3 rounded font-semibold"
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Import Modal */}
      {showExcelImport && (
        <ExcelImport
          raceId={importRaceId}
          seriesClass={currentClass}
          token={token}
          onClose={() => setShowExcelImport(false)}
          onSuccess={loadSeries}
        />
      )}

      {/* MyRCM Import Modal */}
      {showMyRCMImportModal && (
        <MyRCMImport
          showMyRCMImportModal={showMyRCMImportModal}
          setShowMyRCMImportModal={setShowMyRCMImportModal}
          myrcmImportRaceId={myrcmImportRaceId}
          seriesClass={currentClass}
          token={token}
          loadSeries={loadSeries}
        />
      )}

      {/* LiveRC Import Modal */}
      {showLiveRCImportModal && (
        <LiveRCImport
          raceId={livercImportRaceId}
          seriesClass={currentClass}
          token={token}
          onClose={() => setShowLiveRCImportModal(false)}
          loadSeries={loadSeries}
        />
      )}
    </>
  );
}

export default RaceManagement;